/**
 * System state store
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { monitoringApi } from '../services/api/monitoring.api';
import { workflowApi } from '../services/api/workflow.api';
import { trackEvent } from '../lib/analytics';
import { isCurrentAuthUserAdmin } from '../lib/authSession';
import type { SystemStats } from '../types/api.types';

interface SystemState {
  // State
  stats: SystemStats | null;
  isLoading: boolean;
  error: string | null;
  isSchedulerRunning: boolean;
  lastWorkflowRunId: string | null;

  // Actions
  fetchStats: () => Promise<void>;
  fetchSchedulerStatus: () => Promise<void>;
  startScheduler: (config?: { interval_seconds?: number; sources?: string[]; keyword?: string }) => Promise<void>;
  stopScheduler: () => Promise<void>;
  runPipeline: () => Promise<void>;
  reset: () => void;
}

export const useSystemStore = create<SystemState>()(
  devtools(
    (set, get) => ({
      // Initial State
      stats: null,
      isLoading: false,
      error: null,
      isSchedulerRunning: false,
      lastWorkflowRunId: null,

      // Actions
      fetchStats: async () => {
        set({ isLoading: true, error: null });
        trackEvent('system_stats_requested', { source: 'system_store' });
        try {
          const stats = await monitoringApi.getDashboardStats();
          trackEvent('system_stats_loaded', {
            source: 'system_store',
            system_status: stats?.systemStatus || 'unknown',
          });
          set({
            stats,
            isLoading: false,
            isSchedulerRunning: stats?.systemStatus === 'running',
          });
        } catch (error: any) {
          trackEvent('system_stats_failed', { source: 'system_store' });
          console.error('Failed to fetch stats:', error);
          set({
            error: error.message || 'Failed to load dashboard stats',
            isLoading: false,
          });
        }
      },

      fetchSchedulerStatus: async () => {
        if (!isCurrentAuthUserAdmin()) {
          set({ isSchedulerRunning: false });
          return;
        }
        trackEvent('scheduler_status_requested', { source: 'system_store' });
        try {
          const status = await workflowApi.getSchedulerStatus();
          trackEvent('scheduler_status_loaded', {
            source: 'system_store',
            scheduler_running: Boolean(status.scheduler_running || false),
          });
          set({ isSchedulerRunning: status.scheduler_running || false });
        } catch (error: any) {
          trackEvent('scheduler_status_failed', { source: 'system_store' });
          console.error('Failed to fetch scheduler status:', error);
        }
      },

      startScheduler: async (config?: { interval_seconds?: number; sources?: string[]; keyword?: string }) => {
        set({ isLoading: true, error: null });
        trackEvent('scheduler_start_submit', {
          source: 'system_store',
          interval_seconds: config?.interval_seconds ?? 300,
          source_count: config?.sources?.length ?? 0,
        });
        try {
          await workflowApi.startScheduler(config);
          set({ isSchedulerRunning: true, isLoading: false });
          trackEvent('scheduler_start_success', { source: 'system_store' });
          setTimeout(() => get().fetchSchedulerStatus(), 500);
        } catch (error: any) {
          trackEvent('scheduler_start_failed', { source: 'system_store' });
          set({ error: error.message || 'Failed to start scheduler', isLoading: false });
          throw error;
        }
      },

      stopScheduler: async () => {
        set({ isLoading: true, error: null });
        trackEvent('scheduler_stop_clicked', { source: 'system_store' });
        try {
          await workflowApi.stopScheduler();
          set({ isSchedulerRunning: false, isLoading: false });
          trackEvent('scheduler_stop_success', { source: 'system_store' });
          setTimeout(() => get().fetchSchedulerStatus(), 500);
        } catch (error: any) {
          trackEvent('scheduler_stop_failed', { source: 'system_store' });
          set({ error: error.message || 'Failed to stop scheduler', isLoading: false });
          throw error;
        }
      },

      runPipeline: async () => {
        set({ isLoading: true, error: null });
        trackEvent('pipeline_run_clicked', { source: 'system_store' });
        try {
          const runResult = await workflowApi.run();
          get().fetchStats();
          trackEvent('pipeline_run_success', { source: 'system_store' });
          set({
            isLoading: false,
            lastWorkflowRunId: runResult.workflow_id,
          });
        } catch (error: any) {
          trackEvent('pipeline_run_failed', { source: 'system_store' });
          set({ error: error.message || 'Failed to run pipeline', isLoading: false });
          throw error;
        }
      },

      reset: () => {
        set({
          stats: null,
          isLoading: false,
          error: null,
          isSchedulerRunning: false,
          lastWorkflowRunId: null,
        });
      },
    }),
    { name: 'SystemStore' }
  )
);
